// ---------------------------------------------------------------------------
// shortcutsHelp.js — keyboard help overlay + the two shortcut behaviours the
// foundation intentionally left to a feature (spec H).
//
// The foundation already binds the core Canva set through registerCommand (undo/
// redo, Ctrl+A, Ctrl+C/D, Delete, V/T/R/L, Ctrl+]/[ and Ctrl+Alt+]/[, 1px /
// Shift+10px arrow nudge, Ctrl +/-/0 zoom) and the viewport owns wheel-zoom +
// space/middle-mouse pan. This module adds the pieces those don't cover:
//   * a "?" help overlay documenting every binding (extensible by other agents
//     via editor.registerShortcutHelp(...)),
//   * Alt+drag = duplicate (a mouse interaction, not a key — cannot be a
//     registerCommand). It clones the drag target in place so the ORIGINAL is
//     what you drag away, leaving a copy behind (Illustrator/Canva behaviour).
//
// Ownership: this binds an extra 'mouse:down' / 'mouse:up' handler (fabric allows
// multiple listeners; only object:moving is single-owner and we DON'T touch it).
// It never attaches a window keydown listener — the "?" trigger goes through the
// foundation's registerCommand seam.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { registerOverlay } from './overlayHost';

const BASE_HELP = [
  { group: 'General', items: [
    { keys: ['Ctrl', 'Z'], label: 'Anuleaza' },
    { keys: ['Ctrl', 'Shift', 'Z'], label: 'Refa' },
    { keys: ['Ctrl', 'A'], label: 'Selecteaza tot' },
    { keys: ['Ctrl', 'C'], label: 'Copiaza' },
    { keys: ['Ctrl', 'V'], label: 'Lipeste' },
    { keys: ['Ctrl', 'D'], label: 'Dubleaza' },
    { keys: ['Del'], label: 'Sterge' },
    { keys: ['?'], label: 'Aceasta fereastra' },
  ] },
  { group: 'Instrumente', items: [
    { keys: ['V'], label: 'Selectare' },
    { keys: ['T'], label: 'Text' },
    { keys: ['R'], label: 'Dreptunghi' },
    { keys: ['L'], label: 'Linie' },
  ] },
  { group: 'Aranjare', items: [
    { keys: ['↑ ↓ ← →'], label: 'Muta 1px' },
    { keys: ['Shift', '↑ ↓ ← →'], label: 'Muta 10px' },
    { keys: ['Ctrl', ']'], label: 'Un pas in fata' },
    { keys: ['Ctrl', '['], label: 'Un pas in spate' },
    { keys: ['Ctrl', 'Alt', ']'], label: 'Adu in fata' },
    { keys: ['Ctrl', 'Alt', '['], label: 'Trimite in spate' },
    { keys: ['Alt', '+ tragere'], label: 'Duplica prin tragere' },
  ] },
  { group: 'Vizualizare', items: [
    { keys: ['Ctrl', '+'], label: 'Mareste' },
    { keys: ['Ctrl', '−'], label: 'Micsoreaza' },
    { keys: ['Ctrl', '0'], label: 'Zoom 100%' },
    { keys: ['Rotita'], label: 'Zoom la cursor' },
    { keys: ['Space', '+ tragere'], label: 'Panoramare' },
  ] },
];

function HelpOverlay({ editor }) {
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);

  // Single source of truth for the open flag (state + ref + editor mirror), so
  // the imperative toggle never runs side effects inside a state updater.
  const setOpenBoth = useCallback((v) => {
    openRef.current = v;
    editor._helpOpen = v;
    setOpen(v);
  }, [editor]);

  useEffect(() => {
    editor.overlays.help = () => {
      const next = !openRef.current;
      if (next) {
        // Claim a lightweight mode ONLY when no canvas mode is active, so Escape
        // (foundation stage-1) closes the help instead of the editor — without
        // disturbing an in-progress crop/mask.
        if (!editor.getMode()) editor.enterMode('help', { onExit: () => setOpenBoth(false) });
        setOpenBoth(true);
      } else if (editor.isMode('help')) {
        editor.exitMode('help'); // onExit -> setOpenBoth(false)
      } else {
        setOpenBoth(false);
      }
    };
    return () => { if (editor.overlays) delete editor.overlays.help; };
  }, [editor, setOpenBoth]);

  if (!open) return null;

  const close = () => {
    if (editor.isMode('help')) editor.exitMode('help'); // onExit -> setOpenBoth(false)
    else setOpenBoth(false);
  };

  const groups = BASE_HELP.map((g) => ({ ...g, items: g.items.slice() }));
  (editor._extraHelp || []).forEach((extra) => {
    let g = groups.find((x) => x.group === extra.group);
    if (!g) { g = { group: extra.group, items: [] }; groups.push(g); }
    g.items.push({ keys: extra.keys, label: extra.label });
  });

  return (
    <div className="de-ov-backdrop de-ov-help" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="de-ov-dialog de-help-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="de-ov-header">
          <h3>Scurtaturi tastatura</h3>
          <button className="de-ov-close" onClick={close} title="Inchide">&#10005;</button>
        </div>
        <div className="de-ov-body">
          <div className="de-help-cols">
            {groups.map((g) => (
              <React.Fragment key={g.group}>
                <div className="de-help-group-title">{g.group}</div>
                {g.items.map((it, i) => (
                  <div className="de-help-row" key={`${g.group}-${i}`}>
                    <span className="de-help-label">{it.label}</span>
                    <span className="de-help-keys">
                      {it.keys.map((k, j) => <span className="de-kbd" key={j}>{k}</span>)}
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="de-ov-footer">
          <span className="de-ov-footer-note">Apasa ? oricand ca sa deschizi sau inchizi aceasta fereastra.</span>
          <button className="de-btn de-btn-primary" onClick={close}>Am inteles</button>
        </div>
      </div>
    </div>
  );
}

// --- Alt+drag duplicate ------------------------------------------------------
function installAltDragDuplicate(editor) {
  const canvas = editor.canvas;
  let altState = null;

  const onMouseDown = (opt) => {
    const e = opt && opt.e;
    if (!e || !e.altKey || e.button !== 0) return;
    if (!editor.isSelectionEnabled()) return;   // not while free-drawing / panning tool
    if (editor.getMode()) return;               // not inside crop / mask / help
    if (canvas.isDrawingMode) return;
    const target = opt.target;
    // Single objects only — cloning an activeSelection mid-drag is fragile.
    if (!target || target.selectable === false || target.type === 'activeSelection') return;

    // Capture the pre-drag position NOW; the async clone lands the copy there
    // while fabric drags the original away.
    altState = { left: target.left, top: target.top };
    target.clone((cloned) => {
      if (!cloned) return;
      cloned.set({ left: altState ? altState.left : target.left, top: altState ? altState.top : target.top, evented: true });
      const idx = canvas.getObjects().indexOf(target);
      if (idx >= 0) canvas.insertAt(cloned, idx, false); else canvas.add(cloned);
      canvas.requestRenderAll();
      // Debounced: if a drag follows, the foundation's object:modified snapshot
      // captures clone+move as one entry and this collapses to a no-op; a plain
      // alt-click (no drag) records the duplicate on its own.
      editor.pushHistoryDebounced('Dublare');
    }, editor.getSerializationProps());
  };

  const onMouseUp = () => { altState = null; };

  canvas.on('mouse:down', onMouseDown);
  canvas.on('mouse:up', onMouseUp);
}

export function registerShortcutsHelp(editor) {
  if (!editor._extraHelp) editor._extraHelp = [];
  // Optional seam other feature agents (crop / layers / text) can call to add
  // their bindings to the help overlay: registerShortcutHelp(group, keys, label).
  editor.registerShortcutHelp = (group, keys, label) => {
    editor._extraHelp.push({ group, keys: Array.isArray(keys) ? keys : [keys], label });
  };

  registerOverlay(editor, 'help', HelpOverlay);

  editor.registerCommand(['?', 'shift+/'], (ed) => { if (ed.overlays && ed.overlays.help) ed.overlays.help(); });

  editor.addToolButton({
    id: 'de-help',
    title: 'Scurtaturi tastatura (?)',
    icon: '?',
    isActive: () => !!editor._helpOpen,
    onClick: (ed) => { if (ed.overlays && ed.overlays.help) ed.overlays.help(); },
  });

  installAltDragDuplicate(editor);
}
